import { checkBulkFlow, checkIsInternal } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';

const _httpService = new HttpService();

// Set base URL based on user type
if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL') || '');
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL') || '');
}

/**
 * Interface for Tax ID data
 */
export interface TinData {
  name: string;
  value: string;
  disable?: boolean;
}

interface RawTinData {
  name?: string;
  TINName?: string;
  TINNAME?: string;
  value?: string;
  Tin?: string;
  TIN?: string;
}

interface ParsedPsmsState {
  taxIdOptions: TinData[];
}

interface MultiTinPayload {
  RequestedJourney: string;
  ProvTinListFilter: {
    Tin: string;
  };
}

interface RegularTinPayload {
  RequestedJourney: string;
  ProvTinListFilter: {
    ProviderSysKey: string;
  };
}

interface ApiResponseElement {
  TinName?: string;
  name?: string;
  Tin?: string;
  value?: string;
  TINNAME?: string;
}

interface ApiResponse {
  Data?: ApiResponseElement[];
  All?: ApiResponseElement[];
}

// ---------------------------------------------------------------------------
// TIN API cache — module-level, so it survives across re-renders.
// Only the HTTP call path is cached; sessionStorage paths are always read fresh.
// ---------------------------------------------------------------------------
let _tinApiCache: { data: TinData[]; expiresAt: number } | null = null;
let _inFlightTinFetch: Promise<TinData[]> | null = null;

/**
 * Invalidate the TIN API result cache.
 * Call this before injectTinDropdown whenever the provider/TIN context changes
 * (e.g. TaxIDSyncAng, ExitProviderView, simulation mode change, TIN selection).
 * This forces the next fetchTaxIdDropdownData API call to use fresh sessionStorage context.
 */
export const invalidateTinCache = (): void => {
  _tinApiCache = null;
};

/**
 * Internal: fetch TINs from API with a 10-second TTL cache and in-flight deduplication.
 * Multiple concurrent callers within the TTL window share a single HTTP request.
 * Cache is invalidated explicitly via invalidateTinCache() on provider/TIN context changes.
 */
const _fetchTinsFromApiWithCache = async (): Promise<TinData[]> => {
  // Return cached result if still within TTL (covers the initial render burst window)
  if (_tinApiCache && Date.now() < _tinApiCache.expiresAt) {
    return _tinApiCache.data;
  }
  // Dedup: share the in-flight promise so concurrent callers make only one HTTP request
  if (_inFlightTinFetch) return _inFlightTinFetch;

  _inFlightTinFetch = (async (): Promise<TinData[]> => {
    const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
    const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');

    let apiEndpoint: string;
    let payload: MultiTinPayload | RegularTinPayload;

    if (multiTinNavigation === 'true' && multiSelectedTins && !checkBulkFlow()) {
      // Multi-TIN mode: Use extended TIN endpoint
      apiEndpoint = '/extended-tins/top-filter?items=MULTITIN';
      payload = {
        RequestedJourney: 'provTinListFilter',
        ProvTinListFilter: {
          Tin: multiSelectedTins,
        },
      };
    } else {
      // Regular mode: Use standard search endpoint
      apiEndpoint = '/standard-search/topfilter';

      // Get ProviderKey from currentUser (same as Angular implementation)
      let providerSysKey = '';
      try {
        const currentUser = sessionStorage.getItem('currentUser');
        if (currentUser) {
          const parsedUser = JSON.parse(currentUser);
          providerSysKey = parsedUser[0]?.ProviderKey || '';
        }
      } catch (error) {
        console.error('[NavigationBar.service] Error getting ProviderKey from currentUser:', error);
      }

      payload = {
        RequestedJourney: 'provTinListFilter',
        ProvTinListFilter: {
          ProviderSysKey: providerSysKey,
        },
      };
    }

    const response: ApiResponse = await _httpService.push(apiEndpoint, payload);

    // Process response data - handle all TINs from API
    const tinDataSource = response?.Data || response?.All || [];
    const formattedTinData: TinData[] = [];

    tinDataSource.forEach((element: ApiResponseElement) => {
      formattedTinData.push({
        name: element.TINNAME || element.name || 'Tax ID Name not available',
        value: element.Tin || element.value || '',
        disable: (element as ApiResponseElement & { disable?: boolean }).disable ?? false,
      });
    });

    // Sort by TIN value
    formattedTinData.sort((a, b) => a.value.localeCompare(b.value));

    // Store in sessionStorage for future use (preserves existing behaviour)
    if (formattedTinData.length > 0) {
      const stateData = sessionStorage.getItem('state');
      if (stateData) {
        const parsedState = JSON.parse(stateData);
        parsedState.tinsInfo = formattedTinData;
        sessionStorage.setItem('state', JSON.stringify(parsedState));
      }
    }

    // Cache the result for 10 seconds (covers the initial render burst window)
    _tinApiCache = { data: formattedTinData, expiresAt: Date.now() + 10_000 };

    console.info('[NavigationBar.service] Fetched all TINs from API:', formattedTinData);
    return formattedTinData;
  })().finally(() => {
    _inFlightTinFetch = null;
  });

  return _inFlightTinFetch;
};

/**
 * Fetch Tax ID dropdown data for simulation mode OR from Redux Tins state
 * Supports both multi-TIN and regular mode
 *
 * Priority:
 * 1. Check if tinsInfo exists in sessionStorage (populated by Redux/Angular)
 * 2. Check mpinTinData in sessionStorage (external flow - TIN level experience)
 * 3. If not, make API call (cached + deduplicated via _fetchTinsFromApiWithCache)
 */
export const fetchTaxIdDropdownData = async (): Promise<TinData[]> => {
  try {
    if (sessionStorage.getItem('contractIdNavigation') === 'true') {
      return [];
    }

    // Check if tinsInfo is set in state (actively filtered, e.g. by NPI selection)
    // This takes priority because it represents a user-applied filter
    const stateData = sessionStorage.getItem('state');
    if (stateData) {
      try {
        const parsedState = JSON.parse(stateData);
        if (
          parsedState.tinsInfo &&
          Array.isArray(parsedState.tinsInfo) &&
          parsedState.tinsInfo.length > 0
        ) {
          const formattedTinData: TinData[] = parsedState.tinsInfo.map(
            (tin: TinData | ApiResponseElement) => ({
              name:
                (tin as TinData).name ||
                (tin as ApiResponseElement).TINNAME ||
                'Tax ID Name not available',
              value: (tin as TinData).value || (tin as ApiResponseElement).Tin || '',
              disable: (tin as TinData).disable ?? false,
            })
          );
          //console.info('[NavigationBar.service] Fetched TINs from state.tinsInfo:', formattedTinData);

          if (
            formattedTinData.length === 1 &&
            formattedTinData[0].name === 'Tax ID Name not available'
          ) {
            const getCorrectTin = parsedState.selectedCorporateTin;
            if (getCorrectTin) {
              formattedTinData[0] = {
                name: getCorrectTin.TinName || getCorrectTin.name || formattedTinData[0].name,
                value: formattedTinData[0].value,
                disable: formattedTinData[0].disable,
              };
            }
          }
          return formattedTinData;
        }
      } catch (error) {
        console.error('[fetchTaxIdDropdownData] Error parsing state:', error);
      }
    }

    // Next, try to get TINs directly from currentUser (most reliable for external users)

    // dont remove this code, it wont be available in on-prem and needed for AWS
    const currentUser = sessionStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const parsedUser = JSON.parse(currentUser);
        const userTins = parsedUser[0]?.Tins;

        if (userTins && Array.isArray(userTins) && userTins.length > 0) {
          const formattedTinData: TinData[] = userTins.map((tin: RawTinData) => ({
            name: tin.name || tin.TINName || tin.TINNAME || 'Tax ID Name not available',
            value: tin.value || tin.Tin || tin.TIN || '',
          }));
          return formattedTinData;
        }
      } catch (error) {
        console.error('[fetchTaxIdDropdownData] Error parsing currentUser:', error);
      }
    }

    // Check mpinTinData in sessionStorage (external flow - TIN level experience)

    // Check mpinTinData in sessionStorage (external flow - TIN level experience)
    const mpinTinDataStr = sessionStorage.getItem('mpinTinData');
    if (mpinTinDataStr) {
      try {
        const mpinTinData = JSON.parse(mpinTinDataStr);
        if (Array.isArray(mpinTinData) && mpinTinData.length > 0) {
          // Map all mpins - handle both formats (name/value and Tinname/Tin)
          const formattedTinData: TinData[] = mpinTinData.map(
            (tin: {
              name?: string;
              Tinname?: string;
              TINNAME?: string;
              TinName?: string;
              value?: string;
              Tin?: string;
              disable?: boolean;
            }) => ({
              name:
                tin.name ||
                tin.Tinname ||
                tin.TINNAME ||
                tin.TinName ||
                'Tax ID Name not available',
              value: tin.value || tin.Tin || '',
              disable: tin.disable ?? false,
            })
          );
          console.info(
            '[NavigationBar.service] Fetched all TINs from mpinTinData:',
            formattedTinData
          );
          return formattedTinData;
        }
      } catch (parseError) {
        console.error('[NavigationBar.service] Error parsing mpinTinData:', parseError);
      }
    }

    // All sessionStorage paths exhausted — delegate to the cached/deduped API helper
    return await _fetchTinsFromApiWithCache();
  } catch (error) {
    console.error('[NavigationBar.service] Error fetching Tax ID dropdown data:', error);
    if (error instanceof Error) {
      console.error('[NavigationBar.service] Error message:', error.message);
      console.error('[NavigationBar.service] Error stack:', error.stack);
    }
    // Return empty array on error - fallback to manual handling
    return [];
  }
};

/**
 * Update selected Tax ID in sessionStorage and state
 * Updates in both Angular format (Tin/TinName) and React format (value/name) for compatibility
 */
export const updateSelectedTaxId = (tinData: TinData): void => {
  try {
    // Create object with both formats for compatibility
    const tinDataWithBothFormats = {
      value: tinData.value,
      name: tinData.name,
      Tin: tinData.value,
      TinName: tinData.name,
    };

    // Save to topFilterTin with both formats
    sessionStorage.setItem('topFilterTin', JSON.stringify(tinDataWithBothFormats));
    localStorage.setItem('topFilterTin', JSON.stringify(tinDataWithBothFormats));

    //  FIX: Also set selectedTaxId and selectedTinName for Gold Card cover page flow
    sessionStorage.setItem('selectedTaxId', tinData.value);
    sessionStorage.setItem('selectedTinName', tinData.name);

    // Update state.selectedCorporateTin
    const stateData = sessionStorage.getItem('state');
    if (stateData) {
      const parsedState = JSON.parse(stateData);
      parsedState['selectedCorporateTin'] = tinDataWithBothFormats;

      // Angular parity: keep state.topFilterTin in sync for consumers that read from state payload.
      parsedState['topFilterTin'] = {
        Tin: tinData.value,
        TinName: tinData.name,
      };

      // Angular parity: in simulation/HCO flows some consumers read from state.uhc.selectedCorporateTin.
      if (!parsedState.uhc) {
        parsedState.uhc = {};
      }
      parsedState.uhc.selectedCorporateTin = tinDataWithBothFormats;

      sessionStorage.setItem('state', JSON.stringify(parsedState));
    }

    // Invalidate provider-scoped API caches so useMenuRoles re-fetches after reload
    sessionStorage.removeItem('pcor');
    sessionStorage.removeItem('adm');
    sessionStorage.removeItem('aff');
    sessionStorage.removeItem('cesf');
    sessionStorage.removeItem('menuRoles');

    // Update currentUser[0].selectedTin with both formats
    const currentUser = sessionStorage.getItem('currentUser');
    if (currentUser) {
      const parsedUser = JSON.parse(currentUser);
      if (parsedUser[0]) {
        parsedUser[0].selectedTin = tinDataWithBothFormats;
        // Do NOT update HealthCareOrganizationName - preserve original provider organization name
        // This matches Angular simulation mode behavior where only selectedTin is updated
        sessionStorage.setItem('currentUser', JSON.stringify(parsedUser));
      }
    }

    if (
      sessionStorage.getItem('simulationMode') === 'true' &&
      window.location?.pathname.startsWith('/PSMS')
    ) {
      const psmsFilterState = sessionStorage.getItem('psmsFilterState');
      if (psmsFilterState) {
        const parsedPsmsFilterState = JSON.parse(psmsFilterState);
        const matchingOption = parsedPsmsFilterState.taxIdOptions?.find(
          (option: TinData) => option.value === tinData.value
        );
        const selectedOption = matchingOption || {
          id: `tin-${tinData.value}`,
          Name: tinData.name,
          value: tinData.value,
          displayName: tinData.value,
          Tin: tinData.value,
          TinName: tinData.name,
        };
        parsedPsmsFilterState.currentFilters = {
          ...parsedPsmsFilterState.currentFilters,
          tin: [selectedOption],
        };
        sessionStorage.setItem('psmsFilterState', JSON.stringify(parsedPsmsFilterState));
      }

      const psmsState = sessionStorage.getItem('psmsState');
      if (psmsState) {
        const parsedPsmsState = JSON.parse(psmsState);
        parsedPsmsState.taxId = [tinData.value];
        sessionStorage.setItem('psmsState', JSON.stringify(parsedPsmsState));
      }
    }
  } catch (error) {
    console.error('Error updating selected Tax ID:', error);
  }
};

/**
 * Get initial selected Tax ID from sessionStorage
 * Handles both Angular format (Tin/TinName) and React format (value/name)
 */
export const getInitialSelectedTaxId = (): TinData | null => {
  try {
    const topFilterTin = sessionStorage.getItem('topFilterTin');
    const isPSMSJounrey = sessionStorage.getItem('isPSMSReportCardClicked') === 'true';
    const simulationMode = sessionStorage.getItem('simulationMode') == 'true';
    // Check topFilterTin - guard against invalid JSON strings like "undefined"
    if (topFilterTin && topFilterTin !== 'undefined' && topFilterTin !== 'null') {
      try {
        const parsedTin = JSON.parse(topFilterTin);

        // Normalize to React format regardless of source format
        const normalizedTin: TinData = {
          value: parsedTin.value || parsedTin.Tin || '',
          name: parsedTin.name || parsedTin.TinName || '',
        };

        if (normalizedTin.value && normalizedTin.name) {
          return normalizedTin;
        }
      } catch (parseError) {
        console.warn('Failed to parse topFilterTin:', parseError);
      }
    }

    // Fallback: try to get from state.selectedCorporateTin
    const stateData = sessionStorage.getItem('state');
    if (stateData && stateData !== 'undefined' && stateData !== 'null') {
      const parsedState = JSON.parse(stateData);
      const psmsFilterStateRaw = sessionStorage.getItem('psmsFilterState');
      let parsedPsmsState: ParsedPsmsState = { taxIdOptions: [] };
      const isPSMSPage = window.location?.href?.includes('PSMS');
      // Safely parse psmsFilterState
      if (
        psmsFilterStateRaw &&
        psmsFilterStateRaw !== 'undefined' &&
        psmsFilterStateRaw !== 'null'
      ) {
        try {
          parsedPsmsState = JSON.parse(psmsFilterStateRaw);
        } catch {
          parsedPsmsState = { taxIdOptions: [] };
        }
      }

      if (parsedState.selectedCorporateTin) {
        const selectedTin = parsedState.selectedCorporateTin;
        const normalizedTin: TinData = {
          value: selectedTin.value || selectedTin.Tin || '',
          name: selectedTin.name || selectedTin.TinName || '',
        };
        if (normalizedTin.value && normalizedTin.name) {
          if (parsedPsmsState?.taxIdOptions?.length > 0 && isPSMSJounrey && simulationMode) {
            const matchingTin = parsedPsmsState.taxIdOptions.find(
              (option: TinData) => option.value === normalizedTin.value
            );
            if (matchingTin) {
              return {
                value: matchingTin.value || normalizedTin.value,
                name: matchingTin.name || normalizedTin.name,
              };
            } else {
              return {
                value: parsedPsmsState.taxIdOptions[0].value || normalizedTin.value,
                name: parsedPsmsState.taxIdOptions[0].name || normalizedTin.name,
              };
            }
          } else if (isPSMSPage) {
            const matchingTin = parsedState.tinsInfo.find(
              (option: TinData) => option.value === normalizedTin.value
            );
            if (matchingTin) {
              return {
                value: matchingTin.value || normalizedTin.value,
                name: matchingTin.name || normalizedTin.name,
              };
            } else {
              return {
                value: parsedPsmsState.taxIdOptions[0].value || normalizedTin.value,
                name: parsedPsmsState.taxIdOptions[0].name || normalizedTin.name,
              };
            }
          }
          return normalizedTin;
        }
      } else if (isPSMSJounrey && simulationMode) {
        if (parsedPsmsState && parsedPsmsState?.taxIdOptions?.length > 0) {
          return {
            value: parsedPsmsState.taxIdOptions[0].value || '',
            name: parsedPsmsState.taxIdOptions[0].name || '',
          };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting initial selected Tax ID:', error);
    return null;
  }
};
